import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  BookOpen, 
  Bookmark,
  BookmarkCheck,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  MessageSquare,
  PenLine,
  Share2,
  Loader2,
  X,
  Sun,
  Moon,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/ai-granth`;

const AI_EXPLANATION_TYPES = [
  { id: 'simple', label: 'Simple Explanation', icon: '💡' },
  { id: 'deep', label: 'Deep Spiritual Meaning', icon: '🧘' },
  { id: 'life_application', label: 'Life Application', icon: '🌱' },
  { id: 'related_mantras', label: 'Related Mantras', icon: '📿' },
  { id: 'comparison', label: 'Compare with Other Traditions', icon: '🌍' }
];

export default function AIGranthChapterPage({ user }) {
  const { bookId, chapterId } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [chapter, setChapter] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [verses, setVerses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [showChapterNav, setShowChapterNav] = useState(false);
  const [bookmarkedVerses, setBookmarkedVerses] = useState(new Set());
  const [userNotes, setUserNotes] = useState({});
  
  // AI Modal State
  const [showAIModal, setShowAIModal] = useState(false);
  const [selectedVerse, setSelectedVerse] = useState(null);
  const [aiLoading, setAILoading] = useState(false);
  const [aiExplanation, setAIExplanation] = useState(null);
  const [selectedExplanationType, setSelectedExplanationType] = useState('simple');

  // Note Modal State
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [noteVerse, setNoteVerse] = useState(null);

  useEffect(() => {
    if (bookId && chapterId) {
      fetchChapter();
      fetchAllChapters();
      if (user) {
        fetchUserData();
      }
    }
  }, [bookId, chapterId, user]);

  const fetchChapter = async () => {
    try {
      const response = await axios.get(`${API}/books/${bookId}/chapters/${chapterId}`);
      setChapter(response.data.chapter);
      setVerses(response.data.verses || []);
      setBook(response.data.book);
    } catch (error) {
      console.error('Failed to fetch chapter:', error);
      toast.error('Failed to load chapter');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllChapters = async () => {
    try {
      const response = await axios.get(`${API}/books/${bookId}`);
      setChapters(response.data.chapters || []);
    } catch (error) {
      console.error('Failed to fetch chapters:', error);
    }
  };

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const [bookmarksRes] = await Promise.all([
        axios.get(`${API}/bookmarks?token=${token}`)
      ]);
      
      const bookmarkedIds = new Set(
        bookmarksRes.data.bookmarks?.map(b => b.bookmark.verse_id) || []
      );
      setBookmarkedVerses(bookmarkedIds);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
    }
  };

  const handleBookmark = async (verse) => {
    if (!user) {
      toast.error('Please login to bookmark verses');
      return;
    }

    const token = localStorage.getItem('dharmbook_token');
    const isBookmarked = bookmarkedVerses.has(verse.id);

    try {
      if (isBookmarked) {
        await axios.delete(`${API}/bookmarks/${verse.id}?token=${token}`);
        setBookmarkedVerses(prev => {
          const newSet = new Set(prev);
          newSet.delete(verse.id);
          return newSet;
        });
        toast.success('Bookmark removed');
      } else {
        await axios.post(
          `${API}/bookmarks?book_id=${bookId}&chapter_id=${chapterId}&verse_id=${verse.id}&token=${token}`
        );
        setBookmarkedVerses(prev => new Set(prev).add(verse.id));
        toast.success('Verse bookmarked');
      }
    } catch (error) {
      toast.error('Failed to update bookmark');
    }
  };

  const handleAskAI = (verse) => {
    if (!user) {
      toast.error('Please login to use AI features');
      return;
    }
    if (!book?.is_ai_enabled) {
      toast.error('AI explanations not available for this book');
      return;
    }
    setSelectedVerse(verse);
    setAIExplanation(null);
    setShowAIModal(true);
  };

  const fetchAIExplanation = async (type) => {
    if (!selectedVerse) return;

    setAILoading(true);
    setSelectedExplanationType(type);
    
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.post(
        `${API}/ai/explain?verse_id=${selectedVerse.id}&explanation_type=${type}&token=${token}`
      );
      setAIExplanation(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get AI explanation');
    } finally {
      setAILoading(false);
    }
  };

  const handleSaveNote = async () => {
    if (!noteVerse || !noteContent.trim()) return;

    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(
        `${API}/notes?book_id=${bookId}&chapter_id=${chapterId}&verse_id=${noteVerse.id}&content=${encodeURIComponent(noteContent)}&token=${token}`
      );
      setUserNotes(prev => ({ ...prev, [noteVerse.id]: noteContent }));
      toast.success('Note saved');
      setShowNoteModal(false);
      setNoteContent('');
      setNoteVerse(null);
    } catch (error) {
      toast.error('Failed to save note');
    }
  };

  const getCurrentChapterIndex = () => {
    return chapters.findIndex(c => c.id === chapterId);
  };

  const getPrevChapter = () => {
    const idx = getCurrentChapterIndex();
    return idx > 0 ? chapters[idx - 1] : null;
  };

  const getNextChapter = () => {
    const idx = getCurrentChapterIndex();
    return idx < chapters.length - 1 ? chapters[idx + 1] : null;
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-[#FDF8F3]'} flex items-center justify-center`}>
        <Loader2 className={`w-8 h-8 ${darkMode ? 'text-amber-400' : 'text-amber-600'} animate-spin`} />
      </div>
    );
  }

  const prevChapter = getPrevChapter();
  const nextChapter = getNextChapter();

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-[#FDF8F3] text-gray-900'}`}>
      {/* Header */}
      <div className={`sticky top-0 z-30 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-amber-100'} border-b shadow-sm`}>
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <button 
                onClick={() => navigate(`/ai-granth/book/${bookId}`)}
                className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-amber-50'}`}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-amber-600'} truncate`}>
                  {book?.title}
                </p>
                <button
                  onClick={() => setShowChapterNav(!showChapterNav)}
                  className="flex items-center gap-1 font-semibold truncate"
                >
                  Chapter {chapter?.chapter_number}
                  <ChevronDown className={`w-4 h-4 transition-transform ${showChapterNav ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-amber-50'}`}
                title={darkMode ? 'Light Mode' : 'Dark Mode'}
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button
                onClick={() => toast.info('Share feature coming soon!')}
                className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-amber-50'}`}
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Chapter Navigation Dropdown */}
          {showChapterNav && (
            <div className={`absolute left-4 right-4 mt-2 max-h-60 overflow-y-auto rounded-xl shadow-xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-amber-100'}`}>
              {chapters.map((ch) => (
                <button
                  key={ch.id}
                  onClick={() => {
                    navigate(`/ai-granth/book/${bookId}/chapter/${ch.id}`);
                    setShowChapterNav(false);
                  }}
                  className={`w-full px-4 py-3 text-left flex items-center gap-3 ${
                    ch.id === chapterId 
                      ? darkMode ? 'bg-amber-900/30' : 'bg-amber-50' 
                      : darkMode ? 'hover:bg-gray-700' : 'hover:bg-amber-50'
                  }`}
                >
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                    ch.id === chapterId 
                      ? 'bg-amber-600 text-white' 
                      : darkMode ? 'bg-gray-700' : 'bg-amber-100'
                  }`}>
                    {ch.chapter_number}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{ch.title}</p>
                    <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-amber-600'}`}>
                      {ch.total_verses} verses
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Chapter Header */}
      <div className={`py-8 px-4 text-center ${darkMode ? 'bg-gray-800/50' : 'bg-amber-50/50'}`}>
        {chapter?.title_hindi && (
          <p 
            className={`text-2xl mb-2 ${darkMode ? 'text-amber-400' : 'text-amber-700'}`}
            style={{ fontFamily: 'Noto Serif Devanagari, serif' }}
          >
            {chapter.title_hindi}
          </p>
        )}
        <h1 className="text-xl font-bold">
          Chapter {chapter?.chapter_number}: {chapter?.title}
        </h1>
        {chapter?.summary && (
          <p className={`mt-3 text-sm max-w-2xl mx-auto ${darkMode ? 'text-gray-400' : 'text-amber-700'}`}>
            {chapter.summary}
          </p>
        )}
      </div>

      {/* Verses */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="space-y-8">
          {verses.map((verse) => (
            <div 
              key={verse.id}
              className={`rounded-2xl p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm border ${darkMode ? 'border-gray-700' : 'border-amber-100'}`}
              data-testid={`verse-${verse.verse_number}`}
            >
              {/* Verse Number */}
              <div className="flex items-center justify-between mb-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${darkMode ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-800'}`}>
                  Verse {verse.verse_number}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleBookmark(verse)}
                    className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-amber-50'}`}
                    title="Bookmark"
                  >
                    {bookmarkedVerses.has(verse.id) ? (
                      <BookmarkCheck className="w-5 h-5 text-amber-600" />
                    ) : (
                      <Bookmark className="w-5 h-5" />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setNoteVerse(verse);
                      setNoteContent(userNotes[verse.id] || '');
                      setShowNoteModal(true);
                    }}
                    className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-amber-50'}`}
                    title="Add Note"
                  >
                    <PenLine className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Original Text */}
              <div 
                className={`text-xl leading-loose mb-4 ${darkMode ? 'text-amber-200' : 'text-amber-900'}`}
                style={{ fontFamily: 'Noto Serif Devanagari, serif' }}
              >
                {verse.original_text.split('\n').map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>

              {/* Transliteration */}
              {verse.transliteration && (
                <p className={`text-sm italic mb-4 ${darkMode ? 'text-gray-400' : 'text-amber-600'}`}>
                  {verse.transliteration}
                </p>
              )}

              {/* Meanings */}
              <div className="space-y-3">
                {verse.hindi_meaning && (
                  <div>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-500' : 'text-amber-500'}`}>
                      हिंदी अर्थ
                    </p>
                    <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                      {verse.hindi_meaning}
                    </p>
                  </div>
                )}
                {verse.english_meaning && (
                  <div>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-500' : 'text-amber-500'}`}>
                      English Meaning
                    </p>
                    <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                      {verse.english_meaning}
                    </p>
                  </div>
                )}
              </div>

              {/* AI Button */}
              {book?.is_ai_enabled && (
                <button
                  onClick={() => handleAskAI(verse)}
                  className={`mt-4 w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors ${
                    darkMode 
                      ? 'bg-amber-900/30 text-amber-400 hover:bg-amber-900/50' 
                      : 'bg-gradient-to-r from-amber-50 to-amber-100 text-amber-800 hover:from-amber-100 hover:to-amber-200'
                  }`}
                  data-testid={`ai-btn-${verse.verse_number}`}
                >
                  <Sparkles className="w-4 h-4" />
                  Ask AI About This Verse
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Chapter Navigation */}
        <div className="flex items-center justify-between mt-12 pb-20">
          {prevChapter ? (
            <Link
              to={`/ai-granth/book/${bookId}/chapter/${prevChapter.id}`}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-amber-50'} border ${darkMode ? 'border-gray-700' : 'border-amber-100'}`}
            >
              <ChevronLeft className="w-5 h-5" />
              <div>
                <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-amber-600'}`}>Previous</p>
                <p className="font-medium">Chapter {prevChapter.chapter_number}</p>
              </div>
            </Link>
          ) : <div />}

          {nextChapter ? (
            <Link
              to={`/ai-granth/book/${bookId}/chapter/${nextChapter.id}`}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-amber-50'} border ${darkMode ? 'border-gray-700' : 'border-amber-100'}`}
            >
              <div className="text-right">
                <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-amber-600'}`}>Next</p>
                <p className="font-medium">Chapter {nextChapter.chapter_number}</p>
              </div>
              <ChevronRight className="w-5 h-5" />
            </Link>
          ) : <div />}
        </div>
      </div>

      {/* AI Explanation Modal */}
      {showAIModal && selectedVerse && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50">
          <div className={`w-full max-w-lg max-h-[80vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`sticky top-0 p-4 border-b ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-amber-100'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                  <h3 className="font-bold">AI Explanation</h3>
                </div>
                <button
                  onClick={() => setShowAIModal(false)}
                  className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-amber-50'}`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-amber-600'}`}>
                Verse {selectedVerse.verse_number}
              </p>
            </div>

            <div className="p-4">
              {/* Explanation Type Selector */}
              <div className="flex flex-wrap gap-2 mb-4">
                {AI_EXPLANATION_TYPES.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => fetchAIExplanation(type.id)}
                    disabled={aiLoading}
                    className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors ${
                      selectedExplanationType === type.id && aiExplanation
                        ? darkMode ? 'bg-amber-900 text-amber-200' : 'bg-amber-600 text-white'
                        : darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-amber-100 hover:bg-amber-200 text-amber-800'
                    }`}
                  >
                    <span>{type.icon}</span>
                    {type.label}
                  </button>
                ))}
              </div>

              {/* Loading State */}
              {aiLoading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
                </div>
              )}

              {/* AI Response */}
              {!aiLoading && aiExplanation && (
                <div className={`prose prose-sm max-w-none ${darkMode ? 'prose-invert' : ''}`}>
                  <ReactMarkdown>{aiExplanation.explanation}</ReactMarkdown>
                  
                  <div className={`mt-4 p-3 rounded-lg text-xs ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-amber-50 text-amber-700'}`}>
                    {aiExplanation.disclaimer}
                  </div>
                </div>
              )}

              {/* Initial State */}
              {!aiLoading && !aiExplanation && (
                <div className="text-center py-8">
                  <Sparkles className={`w-12 h-12 mx-auto mb-4 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                  <p className={darkMode ? 'text-gray-400' : 'text-amber-700'}>
                    Select an explanation type above to get AI insights
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Note Modal */}
      {showNoteModal && noteVerse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className={`w-full max-w-md rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-amber-100'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <PenLine className="w-5 h-5 text-amber-600" />
                  <h3 className="font-bold">Personal Note</h3>
                </div>
                <button
                  onClick={() => {
                    setShowNoteModal(false);
                    setNoteVerse(null);
                    setNoteContent('');
                  }}
                  className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-amber-50'}`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-amber-600'}`}>
                Verse {noteVerse.verse_number}
              </p>
            </div>

            <div className="p-4">
              <textarea
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                placeholder="Write your thoughts about this verse..."
                className={`w-full h-40 p-3 rounded-xl border resize-none focus:outline-none focus:ring-2 focus:ring-amber-500 ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-500' 
                    : 'bg-white border-amber-200 placeholder-amber-400'
                }`}
              />
              <button
                onClick={handleSaveNote}
                disabled={!noteContent.trim()}
                className="w-full mt-4 py-3 bg-amber-600 text-white font-medium rounded-xl hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save Note
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
